import { icon } from '@fortawesome/fontawesome-svg-core';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import type { GeneAndCopyNumber } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import { SmallExternalLinkIcon } from '@resistapp/client/hooks/use-science-reference';
import type { AssayInfo } from '@resistapp/common/assays';
import { type L2TargetOrAssay, type L2Targets } from '@resistapp/common/assays';
import { MinorTarget } from '@resistapp/common/assays-temp-96-gene-minor-targets';
import { friendlyFoldChange } from '@resistapp/common/friendly';
import { CardCategoryStat, MetricMode } from '@resistapp/common/types';
import { uniq, uniqBy, values } from 'lodash';
import { useLayoutEffect, useRef, useState } from 'react';
import { getGroupColor, getGroupTextColor } from '../shared/palettes';
import { theme } from '../shared/theme';
import { SvgTooltip } from '../tooltips/svg-tooltip';
import type { SingleBarDatum, TargetType } from './site-details/treated-and-reduced-box/types';
const margins = { vertical: 40, horizontal: 140 };

// Approximate text height used for background highlight calculations
const TEXT_HEIGHT = 18;

// Visual sizing constants
const ICON_SIZE = 20; // width & height of the question-mark icon
const ICON_PADDING = 4; // horizontal space between the icon and the text

// Temporary feature gate: disable gene/question mark tooltips until implementation is ready
const QUESTION_TIP_ENABLED = !!(1 - 1);

interface TickLabelProps {
  x: number;
  y: number;
  formattedValue: string;
  identifier: L2TargetOrAssay;
  isSelected: boolean;
  isTheOnlySelected: boolean;
  geneData?: SingleBarDatum;
  groupingTarget?: TargetType;
  onMouseEnter: (e: React.MouseEvent, identifier: L2TargetOrAssay) => void;
  hoveredTarget: L2TargetOrAssay | GeneAndCopyNumber | undefined | null;
  hoveredRowTarget: L2TargetOrAssay | null;
  containerWidth?: number;
  onClick?: () => void;
}

export function TickLabel({
  x,
  y,
  formattedValue,
  identifier,
  isSelected,
  isTheOnlySelected,
  geneData,
  groupingTarget,
  onMouseEnter,
  hoveredTarget,
  hoveredRowTarget,
  containerWidth,
  onClick,
}: TickLabelProps) {
  const { allGeneGroups, getAssayInfo } = useAssayContext();
  const { metricMode } = useOverviewContext();
  const [hoveredTooltipTarget, setHoveredTooltipTarget] = useState<L2TargetOrAssay | null>(null);
  const tooltipContent = buildTooltipContent(formattedValue, geneData, getAssayInfo);

  // --- Measure text width so we can position the icon flush against the label ---
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  useLayoutEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      if (bbox.width && bbox.width !== textWidth) {
        setTextWidth(bbox.width);
      }
    }
  }, [formattedValue, textWidth]);

  const onMouseEnterIcon = (_event: React.MouseEvent, targetIdentifier: L2TargetOrAssay) => {
    setHoveredTooltipTarget(targetIdentifier);
  };
  const onMouseLeaveIcon = () => {
    setHoveredTooltipTarget(null);
  };

  if (metricMode === MetricMode.ARGI || metricMode === MetricMode.RISK) {
    const groupTextColor = getGroupTextColor(
      identifier as L2Targets,
      'antibiotic',
      allGeneGroups as Record<string, string[]>,
    );
    const padding = 6;

    return (
      <g
        onClick={onClick}
        onMouseEnter={e => {
          onMouseEnter(e, identifier);
        }}
      >
        {/* This is transparent rect to control the correct area for detecting hover */}
        <rect x={-margins.horizontal} y={y - 10} width={containerWidth} height={20} fill="transparent" />
        {QUESTION_TIP_ENABLED && (hoveredTarget === identifier || hoveredRowTarget === identifier) && (
          <>
            <foreignObject x={x - textWidth - ICON_SIZE - ICON_PADDING} y={y - 12} width={ICON_SIZE} height={ICON_SIZE}>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                size="lg"
                style={{
                  cursor: 'pointer',
                  color: theme.colors.neutral600,
                }}
              />
            </foreignObject>
            {hoveredTooltipTarget === identifier && (
              <SvgTooltip x={x - textWidth - 100} y={y} content={tooltipContent} onMouseLeave={onMouseLeaveIcon} />
            )}
          </>
        )}
        {QUESTION_TIP_ENABLED && (
          /* This is transparent rect to control the correct area for detecting hover */
          <rect
            x={x - textWidth - ICON_SIZE - ICON_PADDING}
            y={y - 13}
            width={ICON_SIZE}
            height={ICON_SIZE}
            fill="transparent"
            onMouseEnter={e => {
              onMouseEnterIcon(e, identifier);
            }}
          />
        )}
        <text
          ref={textRef}
          x={x - padding + 8}
          y={y}
          fontSize={14}
          fontWeight={isTheOnlySelected ? theme.fontWeight.extraHeavy : theme.fontWeight.bold}
          fontStyle={groupingTarget === MinorTarget.PATHOGEN ? 'italic' : 'normal'}
          cursor="pointer"
          fill={groupTextColor}
          dy=".33em"
          textAnchor="end"
        >
          {formattedValue}
        </text>
      </g>
    );
  } else {
    const groupColor = getGroupColor(identifier, 'antibiotic', allGeneGroups);
    const groupTextColor = getGroupTextColor(identifier, 'antibiotic', allGeneGroups);
    const padding = 6;

    return (
      <g
        onMouseEnter={e => {
          onMouseEnter(e, identifier);
        }}
      >
        {/* This is transparent rect to control the correct area for detecting hover */}
        <rect x={-margins.horizontal} y={y - 10} width={142} height={20} fill="transparent" />
        {QUESTION_TIP_ENABLED && (hoveredTarget === identifier || hoveredRowTarget === identifier) && (
          <>
            <foreignObject x={x - textWidth - ICON_SIZE - ICON_PADDING} y={y - 12} width={ICON_SIZE} height={ICON_SIZE}>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                size="lg"
                style={{
                  cursor: 'pointer',
                  color: theme.colors.neutral600,
                }}
              />
            </foreignObject>
            {hoveredTooltipTarget === identifier && (
              <SvgTooltip
                x={x - textWidth - ICON_SIZE - ICON_PADDING}
                y={y}
                content={tooltipContent}
                onMouseLeave={onMouseLeaveIcon}
              />
            )}
            {/* This is transparent rect to control the correct area for detecting hover */}
            <rect
              x={x - textWidth - ICON_SIZE - ICON_PADDING}
              y={y - 13}
              width={ICON_SIZE}
              height={ICON_SIZE}
              fill="transparent"
              onMouseEnter={e => {
                onMouseEnterIcon(e, identifier);
              }}
            />
          </>
        )}
        {isTheOnlySelected && groupingTarget === 'ARG' && (
          <rect
            x={x - textWidth - padding * 3 + 7}
            y={y - TEXT_HEIGHT / 2 - padding / 2}
            width={textWidth + padding * 3}
            height={TEXT_HEIGHT + padding}
            fill={groupColor}
            rx={theme.borders.radius.small}
          />
        )}
        <text
          ref={textRef}
          x={x + 2}
          y={y}
          fontSize={14}
          fontWeight={isTheOnlySelected ? theme.fontWeight.extraHeavy : theme.fontWeight.bold}
          fontStyle={groupingTarget === MinorTarget.PATHOGEN ? 'italic' : 'normal'}
          fill={
            isTheOnlySelected && groupingTarget === 'ARG'
              ? groupTextColor
              : isSelected
                ? theme.colors.neutral900
                : theme.colors.neutral700
          }
          dy=".33em"
          textAnchor="end"
          cursor="pointer"
          onClick={onClick}
        >
          {formattedValue}
        </text>
      </g>
    );
  }
}

export function useTickLabel() {
  const [hoveredTarget, setHoveredTarget] = useState<L2TargetOrAssay | GeneAndCopyNumber | undefined | null>(null);
  const [hoveredRowTarget, setHoveredRowTarget] = useState<L2TargetOrAssay | null>(null);

  const onMouseEnter = (_event: React.MouseEvent, targetIdentifier: L2TargetOrAssay) => {
    setHoveredRowTarget(targetIdentifier);
  };
  const onMouseLeave = () => {
    setHoveredRowTarget(null);
  };

  return { hoveredTarget, setHoveredTarget, hoveredRowTarget, onMouseEnter, onMouseLeave };
}

function buildTooltipContent(
  formattedValue: string,
  geneData: SingleBarDatum | undefined,
  getAssayInfo: (assay: string) => AssayInfo | undefined,
) {
  if (!geneData) {
    return <div>{formattedValue}</div>;
  }

  const assayInfos = geneData.analysedGenes
    .map(g => getAssayInfo(g.assay))
    .filter((info): info is AssayInfo => info != null);

  // Safely gather CARD matches and AMR family data, guarding against missing card information
  const matches = assayInfos.flatMap(info => (info.card ? info.card.matches : []));
  const families = assayInfos.flatMap(info => (info.card ? info.card.amrFamilies : []));

  if (matches.length === 0 && families.length === 0) {
    return (
      <>
        <div>Genes detected: {geneData.analysedGenes.length}</div>
        <div>Average reduction: {friendlyFoldChange(geneData.log10Reduction, 1, true)}</div>
      </>
    );
  }

  const familyStats = families.reduce<Record<string, CardCategoryStat>>((acc, f) => {
    if (Object.prototype.hasOwnProperty.call(acc, f.name)) {
      acc[f.name].coveredGenes += f.coveredGenes;
    } else {
      acc[f.name] = { ...f };
    }
    return acc;
  }, {});

  const familyText = values(familyStats)
    .map(f => `${f.name} (${f.coveredGenes}/${f.totalGenes})`)
    .join(', ');

  const splitValues = (raw: string | null | undefined) =>
    raw
      ? raw
          .split(';')
          .map(v => v.trim())
          .filter(Boolean)
      : [];

  const mechanisms = uniq(matches.flatMap(m => splitValues(m.resistanceMechanism))).join(', ');

  const uniqueMatches = uniqBy(matches, m => m.cardShortName ?? m.cvtermId);

  const geneElements = uniqueMatches.map((m, idx) => {
    const accession = m.dnaAccession ?? m.proteinAccession;
    return (
      <span key={m.cvtermId ?? m.cardShortName ?? idx}>
        <a href={`https://card.mcmaster.ca/ontology/${m.cvtermId}`} target="_blank" rel="noopener noreferrer">
          {m.cardShortName}
        </a>
        {accession && (
          <a
            href={`https://www.ncbi.nlm.nih.gov/nuccore/${accession}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '2px' }}
          >
            <SmallExternalLinkIcon icon={icon(faExternalLinkAlt)} />
          </a>
        )}
        {idx < uniqueMatches.length - 1 ? ', ' : ''}
      </span>
    );
  });

  return (
    <>
      <div>AMR gene family: {familyText || 'N/A'}</div>
      <div>Resistance Mechanisms: {mechanisms || 'N/A'}</div>
      <div>Card genes: {geneElements.length ? geneElements : 'N/A'}</div>
    </>
  );
}
